import { defineStore } from 'pinia'
import { ref, computed } from 'vue'


import env from '~/libs/env'

import { useBorrowerStore } from '~/store/borrower'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import AnalyticsService from '~/libs/analytics-service'
import retryOnNetworkError from '~/libs/retry-on-network-error'
import { get } from 'es-toolkit/compat'

export const useAppAnalyticsStore = defineStore('appAnalytics', () => {
  const nuxtApp = useNuxtApp()
  const { $lendioCookies, $axios } = nuxtApp

  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const borrowerStore = useBorrowerStore()

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
  */
  const searchedOffers = ref({})
  const trackedCookies = ref({})

  /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
  */

  const serviceAvailable = computed(() => {
    return (tag, liveOnly = false) => {
      if (env('environment') === 'dev') {
        return true
      }
      const checkLive = liveOnly ? env('environment') === 'LIVE' : true
      return checkLive && typeof window[tag] !== 'undefined' && window[tag]
    }
  })

  const getLendioId = computed(() => {
    const dreamsAvailable = serviceAvailable.value('dreams')

    if (dreamsAvailable) {
      return get(window, 'dreams.dataLayer.user.lendioId', null)
    }

    if (window?.localStorage?.dreams) {
      const dreams = JSON.parse(window.localStorage.dreams)
      const lendioId = dreams.user.lendioId
      return lendioId
    }

    return null
  })

  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
  */

  async function dreamsSetEvent(dreamsPayload = {}) {
    const { name, value } = dreamsPayload
    if (name && value && serviceAvailable.value('dreams')) {
      window.dreams.setEvent(name, value)
    }
    return false
  }

  async function setSearchedOffers(payload) {
    const mbox = Object.keys(payload).find(() => true),
    cacheKey = getCacheKey(mbox)
    if (cacheKey) {
      $lendioCookies.set(cacheKey, encodeURIComponent(JSON.stringify(payload[mbox].offer)), payload.options || {secure: env('environment') !== 'dev'})
    }
    searchedOffers.value = { ...searchedOffers.value, ...payload }
  }
  /**
   * Write passed variables to FullStory.
   *
   * These values are formatted according to fullstory's requirements and
   * used to create reporting segments.
   *
   * @param {*} values A Key-Value pair object with values to set in FullStory
   * @returns Promise true/false of success
   */
  async function fsUpdate(values = {}) {
    const errorOptions = {
      functionName: 'fsUpdate',
      values,
    }
    const fsInstance = _getFullstoryInstance(errorOptions)
    if (!fsInstance) {
      return false
    }
    const setUserVars = fsInstance.setUserVars
    if (!setUserVars || !values) {
      throw new Error('Instantiated Fullstory is missing setUserVars')
    }
    const fsValues = Object.keys(values)
      .reduce((carry, key) => {
        let type = 'str'
        const val = values[key]
        if (/^-?\d+$/.test(val)) {
          type = 'int'
        } else if (/^-?\d*\.\d*$/.test(val)) {
          type = 'real'
        } else if (/^(?:true|false)$/.test(val)) {
          type = 'bool'
        }
        carry[`${key}_${type}`] = val
        return carry
      }, {})
    try {
      await setUserVars(fsValues)
    } catch (e) {
      console.error(e)
      return false
    }
    return true
  }

  /**
   * Save state of authenticated user to analytics services.
   *
   * The keys for the passed values cannot have underscores, as those will
   * fail in Fullstory validation.
   *
   * @param {*} values Key-Value pairings of the values passed
   * @returns Object with success boolean for each service. Currently only `fullstory` after pendo removed.
   */
  async function saveSharedState(values = {}) {
    // Ignore non-authenticated borrowers
    if (!get(borrowerStore, 'borrower.id')) {
      return { fullstory: false }
    }

    // Save to and fullstory.
    const servicesSaved = {}
    const [ fullstory ] = await Promise.all([
      this.fsUpdate(values)
        .catch(e => false),
    ])
    servicesSaved.fullstory = fullstory
    return servicesSaved
  }

  async function waitForService(tag) {
    return new Promise((resolve, reject) => {
      const maxAttempts = 75 // 75 * 200ms = 15 seconds
      let count = 0
      const checkService = setInterval(() => {
        if (serviceAvailable.value(tag) || count >= maxAttempts) {
          clearInterval(checkService)
          resolve(serviceAvailable.value(tag))
        }
        count++
      }, 200)
    })
  }

  /**
   * Load the tracked cookies (or query parameters) per the cookie service
   */
  async function loadTrackedCookies() {
    trackedCookies.value = $lendioCookies.trackingCookies.map(cookie => {
        return [
          cookie,
          $lendioCookies.get(cookie) || get(nuxtApp, `query["${cookie}"]`)
        ]
      })
      .filter(([cookie, val]) => Boolean(val))
      .reduce((carry, [cookie, val]) => (carry[cookie] = val, carry), {})
  }

  /**
   * Load the tracked cookies (or query parameters) per the cookie service
   */
   async function clearTrackedCookies() {
    trackedCookies.value = {}
    $lendioCookies.trackingCookies.forEach(cookie => $lendioCookies.remove(cookie))
  }

  function _getFullstoryInstance(errorOptions = {}) {
    const fsAvailable = window?._fs_namespace && typeof window[window._fs_namespace] === 'function'
    if (!fsAvailable) {
      const errorDefaults = {
        window: !!window,
        _fs_namespace: window._fs_namespace ?? 'none',
      }
      console.warn('Fullstory not available', { ...errorDefaults, ...errorOptions })
      return null
    }
    return window[window._fs_namespace]
  }

  async function fsTrackEvent({ name, properties = {}}) {
    if (!name) {
      return log.warning('Name required to post Fullstory event.')
    }
    const errorOptions = {
      functionName: 'fsTrackEvent',
      name,
      properties
    }
    const fsInstance = _getFullstoryInstance(errorOptions)
    if (!fsInstance) {
      return
    }
    try {
      fsInstance('trackEvent', {
        name,
        properties
      })
    } catch (err) {
      errorOptions.fullstory = fsInstance ?? 'not available'
      errorOptions.err = err
      return log.warning('Error posting Fullstory event', errorOptions)
    }
  }
/*
  Create faux tracking route in borrowerPagePortalPageViews
  Useful for tracking component stage changes that do not actually change page/url
  e.g. Manual upload state on /marketplace-app/documents/ would be faux route /marketplace-app/documents?state=manual
 */
  async function fauxBorrowerPageTrackingEvent(fauxRouteName) {
    if (!fauxRouteName) return
    await waitForService('dreams')
    AnalyticsService.trackRoute(
      borrowerStore.borrower ? borrowerStore.borrower.id : null,
      getLendioId.value,
      fauxRouteName,
      $axios
    )
  }

  async function trackViewedFinancingOption (optionId, trackingData = {}) {
    const [type, id] = `${optionId}`.split('-')
    const normalizedType = type === 'option' ? 'offerOption' : type
    if (!normalizedType) {
      throw new Error('Invalid financing option type')
    }
    if (!id) {
      throw new Error('Financing option id is not defined')
    }

    const trackingProps = typeof trackingData === 'object' ? trackingData : {}
    const url = `${env('apiUrl')}/options/${normalizedType}/${id}/viewed`;

    return $axios.post(url, trackingProps, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  async function registerQuickbooksProductInteraction(productType, interaction) {
    const requestBody = {
      'productType': productType,
      'interaction': interaction,
    }

    try {
      await retryOnNetworkError(
        () => $axios.post(`${env('apiUrl')}/borrower-portal/borrower/${borrowerStore.borrowerId}/quickbooks-product`, requestBody)
      )
    } catch (e) {
      log.error('Unable to register quickbooks interaction', {
        err: e,
        errMessage: e.message,
      })
    }
  }

  function sendDataLayerEvent(eventName, eventParams = {}) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventParams,
      })
    }
  }

  return {
    // STATE
    searchedOffers,
    trackedCookies,

    // GETTERS
    serviceAvailable,
    getLendioId,

    // ACTIONS
    dreamsSetEvent,
    setSearchedOffers,
    fsTrackEvent,
    fsUpdate,
    saveSharedState,
    waitForService,
    loadTrackedCookies,
    clearTrackedCookies,
    fauxBorrowerPageTrackingEvent,
    trackViewedFinancingOption,
    registerQuickbooksProductInteraction,
    sendDataLayerEvent
  }
})
